import { Users, FileText, BarChart3, CreditCard, AlertCircle, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import AdminStatCard from './AdminStatCard';
import { AdminTab } from './AdminSidebar';

interface AdminOverviewProps {
  businessStats: any;
  allProfiles: any[];
  systemErrors: any[];
  setActiveTab: (tab: AdminTab) => void;
}

const AdminOverview = ({ businessStats, allProfiles, systemErrors, setActiveTab }: AdminOverviewProps) => {
  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* High-level Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <AdminStatCard
          title="Total Users"
          value={allProfiles.length}
          icon={Users}
          trend={{ value: 12, label: 'vs last month' }}
        />
        <AdminStatCard
          title="Total Forms"
          value={businessStats?.total_forms || 0}
          icon={FileText}
          trend={{ value: 5, label: 'vs last month' }}
        />
        <AdminStatCard
          title="Submissions"
          value={businessStats?.total_submissions || 0}
          icon={BarChart3}
          trend={{ value: 18, label: 'vs last month' }}
        />
        <AdminStatCard
          title="Revenue (XLM)"
          value={(businessStats?.total_xlm_revenue || 0).toFixed(2)}
          icon={CreditCard}
          trend={{ value: 2, label: 'vs last month' }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Users Table Snippet */}
        <div className="bg-card border border-border rounded-xl shadow-sm lg:col-span-2 flex flex-col overflow-hidden">
          <div className="px-5 py-4 border-b border-border flex flex-row items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">Recent Signups</h3>
            <button 
              onClick={() => setActiveTab('users')}
              className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              View All
            </button>
          </div>
          <div className="p-0 overflow-x-auto flex-1">
            <table className="w-full text-left whitespace-nowrap">
              <thead>
                <tr>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-muted/40">User</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-muted/40">Plan</th>
                  <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground bg-muted/40 text-right">Date</th>
                </tr>
              </thead>
              <tbody>
                {allProfiles.slice(0, 5).map((p, i) => (
                  <tr key={i} className="hover:bg-muted/50 transition-colors group">
                    <td className="px-5 py-3 border-b border-border/50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs uppercase shrink-0 ring-1 ring-primary/20">
                          {p.email?.[0] || 'U'}
                        </div>
                        <span className="text-sm font-medium text-foreground truncate max-w-[150px] sm:max-w-[200px]">{p.email || 'Anonymous'}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 border-b border-border/50">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground capitalize">
                        {p.plan || 'Free'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-muted-foreground text-right border-b border-border/50">
                      {new Date(p.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* System Alerts Snippet */}
        <div className="bg-card border border-border rounded-xl shadow-sm flex flex-col">
          <div className="px-5 py-4 border-b border-border">
            <h3 className="text-base font-semibold text-foreground">Recent Alerts</h3>
          </div>
          <div className="p-5 space-y-4 flex-1">
             {systemErrors.length > 0 ? systemErrors.slice(0, 4).map((err, i) => (
               <div key={i} className="flex gap-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
                  <div className="min-w-0">
                     <p className="text-[11px] font-semibold text-destructive uppercase tracking-widest mb-1">Payment Failure</p>
                     <p className="text-sm text-muted-foreground truncate">{err.reason}</p>
                  </div>
               </div>
             )) : (
               <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm py-8">
                  <ShieldCheck className="w-12 h-12 mb-3 text-emerald-500/30" />
                  <span>No critical alerts</span>
               </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
